// Compact period dropdown for chart card headers.
// Each chart owns its own period state — the picker is local to the card.

import { useEffect, useRef, useState } from 'react'
import { Icon } from './Icon'
import { computePeriod, type PeriodKey } from '../lib/period-context'

const OPTIONS: Array<{ key: PeriodKey; label: string }> = [
  { key: '7d',           label: 'Last 7 days' },
  { key: '30d',          label: 'Last 30 days' },
  { key: 'this_month',   label: 'This month' },
  { key: 'last_month',   label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'ytd',          label: 'Year to date' },
  { key: 'last_year',    label: 'Last year' },
]

interface Props {
  value: PeriodKey
  onChange: (v: PeriodKey) => void
}

export function ChartPeriodPicker({ value, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const current = OPTIONS.find(o => o.key === value)
  const period = computePeriod(value)

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="h-8 px-2.5 inline-flex items-center gap-1.5 border border-ink-200 rounded-lg text-[12.5px] font-medium hover:bg-ink-50 bg-white"
      >
        <Icon name="calendar-range" className="w-3.5 h-3.5 text-ink-500" />
        <span>{current?.label ?? value}</span>
        <Icon name="chevron-down" className="w-3.5 h-3.5 text-ink-400" />
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-30 w-52 card p-1.5">
          {OPTIONS.map(o => (
            <button
              key={o.key}
              onClick={() => { onChange(o.key); setOpen(false) }}
              className={`w-full text-left px-3 py-1.5 rounded-md text-[13px] hover:bg-ink-50 ${value === o.key ? 'bg-ink-50 font-semibold' : ''}`}
            >
              {o.label}
            </button>
          ))}
          <div className="border-t border-ink-100 my-1.5" />
          <div className="px-3 py-1.5 text-[11px] text-ink-500 mono">
            {period.from} → {period.to}
          </div>
        </div>
      )}
    </div>
  )
}

// Tiny helper for callers that need the date range from a key.
export { computePeriod }
